import {
  Colors,
  Divider,
  Flex,
  IconInfo,
  Typography,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { bridge, isNil, isWeb, localizeNumber } from 'shared/src/utils';
import { BrandIconFreeDelivery } from '../BrandIconFreeDelivery';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import type { GoodsOption } from 'shared/src/api';
import { useGoodsOptionsPricing } from '../../queries/useGoodsOptionsPricing';
import { usePostBatchRedeemCoupon } from '../../queries/usePostBatchRedeemCoupon';
import { useIsBottomSheetGiftMode } from '../../hooks/useIsBottomSheetGiftMode';
import { useLogDownloadCoupon } from '../../hooks/useLogCompleteCouponDownload';
import {
  useIsAnonymousUser,
  useIsSoftKeyboardVisible,
  useLogAnalyticsEvent,
} from 'shared/src/hooks';
import { CouponStateTag, type CouponState } from '../CouponStateTag';
import { GoodsParamsContext } from '../../../../context/GoodsParamsContext';
import { useGoodsSno } from '../../../../hooks/useGoodsSno';

const CouponTagView: React.FC<{ selected: GoodsOption[] }> = props => {
  const isAnonymousUser = useIsAnonymousUser();
  const goodsSno = useGoodsSno();
  const injected = useContext(GoodsParamsContext);

  const { data: pricing, isLoading } = useGoodsOptionsPricing({
    options: props.selected.map(option => ({
      sno: option.sno,
      count: option.purchase_count ?? 1,
    })),
  });
  const {
    mutateAsync: postBatchRedeemCoupon,
    isPending: isLoadingPostBatchRedeemCoupon,
  } = usePostBatchRedeemCoupon(props.selected);
  const logCouponDownloadComplete = useLogDownloadCoupon();

  const isAllIssued = useMemo(() => {
    if (!pricing) return false;
    return (
      pricing.coupon_list.filter(coupon => coupon.item.is_issued).length ===
      pricing.coupon_list.length
    );
  }, [pricing]);

  // CLICK CART
  // AI FITTING 없는것 확인
  // Android에서
  const onPressRedeemCoupon = useCallback(async () => {
    if (isAnonymousUser) {
      bridge.assertLogin({
        message: '로그인을 해야 이용 할 수 있습니다.',
        redirect: `/goods/${goodsSno}`,
      });
      return;
    }

    try {
      const downloadableCoupons =
        pricing?.coupon_list.filter(coupon => !coupon.item.is_issued) ?? [];

      if (downloadableCoupons.length === 0) return;

      const response = await postBatchRedeemCoupon();
      const redeemedCouponSnos = new Set(response?.redeemed_coupons);
      //여기서 COMPLETE_COUPON_DOWNLOAD
      downloadableCoupons
        .filter(coupon => redeemedCouponSnos.has(coupon.item.sno))
        .forEach(coupon => {
          logCouponDownloadComplete({ coupon: coupon, isDownloadAll: true });
        });
      bridge.toast('쿠폰이 발급되었습니다');
    } catch (error) {
      const ex = error as Error;
      bridge.toast(ex.message);
    }
  }, [
    logCouponDownloadComplete,
    isAnonymousUser,
    goodsSno,
    postBatchRedeemCoupon,
    pricing?.coupon_list,
  ]);

  const logAnalyticsEvent = useLogAnalyticsEvent();

  useEffect(() => {
    if (isLoading || isNil(pricing)) return;
    if (pricing.coupon_list.length === 0) return;
    if (isAllIssued) return;

    logAnalyticsEvent({
      name: 'DISPLAY_COUPON',
      analytics: {
        ...pricing.coupon?.logging.analytics,
        SCREEN: injected?.logging ? injected.logging.screen : 'GOODS_DETAIL',
      },
    });
  }, [injected, isAllIssued, isLoading, pricing, logAnalyticsEvent]);

  const state = useMemo<CouponState>(() => {
    if (isAnonymousUser) return 'anonymous';
    if (isLoading || isNil(pricing) || isLoadingPostBatchRedeemCoupon) {
      return 'loading';
    }
    if (pricing.coupon_list.length === 0) return 'none';
    if (isAllIssued) return 'issued';
    return 'downloadable';
  }, [
    isAllIssued,
    isAnonymousUser,
    isLoading,
    isLoadingPostBatchRedeemCoupon,
    pricing,
  ]);

  return (
    <CouponStateTag
      state={state}
      couponCount={pricing?.coupon_list.length ?? 0}
      discount={pricing?.discount_amount ?? 0}
      onPressDownload={onPressRedeemCoupon}
    />
  );
};

export const PricingInfo: React.FC<{ selected: GoodsOption[] }> = props => {
  const { data: pricing } = useGoodsOptionsPricing({
    options: props.selected.map(option => ({
      sno: option.sno,
      count: option.purchase_count ?? 1,
    })),
  });
  const goodsPrice = useMemo(() => {
    return props.selected.reduce(
      (acc, cur) => acc + cur.price * (cur.purchase_count ?? 1),
      0,
    );
  }, [props.selected]);
  const discountPrice = useMemo(() => {
    if (!pricing || pricing.coupon_list.length === 0) return 0;
    const isAllIssued = pricing.coupon_list.every(
      coupon => coupon.item.is_issued,
    );
    return isAllIssued ? pricing.discount_amount : 0;
  }, [pricing]);

  const totalPrice = useMemo(() => {
    return goodsPrice - discountPrice;
  }, [discountPrice, goodsPrice]);

  const isGiftMode = useIsBottomSheetGiftMode();
  /**
   * 모웹에서 텍스트 옵션을 입력할 때만 true. 네이티브는 항상 false 라 아래 렌더 트리가
   * 기존과 완전히 동일하다(`useIsSoftKeyboardVisible`).
   *
   * 웹에서 접는 이유: 키보드가 뜨면 그릴 수 있는 높이가 절반 이하로 줄어드는데(실측 641 → 299)
   * 이 블록 175px 과 하단 버튼 81px 이 바닥에 도킹돼 있어 리스트에 43px 밖에 남지 않는다
   * (시트 높이를 어떻게 잡아도 chrome 은 바닥에 붙으므로 높이 배분으로는 해결되지 않는다).
   */
  const keyboardVisible = useIsSoftKeyboardVisible();

  if (props.selected.length === 0) return null;

  if (isGiftMode) {
    return (
      <Flex padding={{ horizontal: 20, vertical: 16 }} gap={12}>
        <Flex.Row alignItems={'center'} gap={4}>
          <IconInfo size={16} fill={Colors.content_secondary} />
          <Typography type={'body2-relaxed'} color={'content_secondary'}>
            모든 선물 상품은 일반 배송으로 부내드려요
          </Typography>
        </Flex.Row>
        <Divider size={'small'} />
        <Flex gap={8}>
          <Flex.Row justifyContent={'space-between'}>
            <Typography type={'subtitle1'}>예상 결제금액</Typography>
            <Typography type={'h4'}>{localizeNumber(totalPrice)}원</Typography>
          </Flex.Row>
        </Flex>
      </Flex>
    );
  }

  const summaryRows = (
    <>
      <Flex.Row
        justifyContent={'space-between'}
        height={30}
        alignItems={'center'}
      >
        <Typography type={'body2'} color={'content_tertiary'}>
          총 {props.selected.length}개 상품
        </Typography>
        <Typography type={'body2'}>{localizeNumber(goodsPrice)}원</Typography>
      </Flex.Row>
      <Flex.Row
        justifyContent={'space-between'}
        height={30}
        alignItems={'center'}
      >
        <Typography type={'body2'} color={'content_tertiary'}>
          쿠폰 할인
        </Typography>
        <CouponTagView selected={props.selected} />
      </Flex.Row>
      <VerticalSpacing size={12} />
      <Divider size={'small'} />
      <VerticalSpacing size={12} />
    </>
  );

  return (
    <Flex padding={{ horizontal: 20, vertical: 16 }}>
      {/* 접기 래퍼는 웹에만 씌운다 — 네이티브 렌더 트리는 기존과 완전히 동일하다.
          언마운트가 아니라 높이만 0 으로 접는 이유는 `CouponTagView` 가 다시 마운트되면
          키보드를 내릴 때 `DISPLAY_COUPON` 로깅이 재발화하기 때문이다. */}
      {isWeb ? (
        <Flex height={keyboardVisible ? 0 : undefined} overflow={'hidden'}>
          {summaryRows}
        </Flex>
      ) : (
        summaryRows
      )}
      <Flex gap={8}>
        <Flex.Row justifyContent={'space-between'}>
          <Typography type={'subtitle1'}>예상 결제금액</Typography>
          <Typography type={'h4'}>{localizeNumber(totalPrice)}원</Typography>
        </Flex.Row>
        {!keyboardVisible && (
          <Flex.Row justifyContent={'flex-end'}>
            <BrandIconFreeDelivery />
          </Flex.Row>
        )}
      </Flex>
    </Flex>
  );
};
